import { Box, Button, Stack, Step, StepLabel, Stepper, Typography } from "@mui/material";
import React from "react";
import { useFetch } from "../../hooks/useFetch";
import { IProfile } from "../../interfaces/IUser";

// <DialogActions>
//     <Button variant="outlined" onClick={() => setShowAssignWorkerDialog(false)}>
//         Abbrechen
//     </Button>
//     <Button
//         variant="contained"
//         onClick={() => {
//             setProjectObject({
//                 ...projectObject,
//                 Worker: [
//                     ...projectObject.Worker,
//                     {
//                         Profile: workerForCustomer,
//                         ProjectRole: {
//                             id: 6,
//                             role: "Außendienst",
//                         },
//                     },
//                 ],
//             });
//             setShowAssignWorkerDialog(false);
//         }}
//     >
//         Zuweisen
//     </Button>
// </DialogActions>

type AussendienstMitarbeiterAssignmentProps = {
    workerForCustomer: IProfile | undefined;
};

const AussendienstMitarbeiterAssignment: React.FC<AussendienstMitarbeiterAssignmentProps> = ({ workerForCustomer }) => {
    return (
        <div>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Das Projekt hat noch keinen Außendienstmitarbeiter zugewiesen.
                </Typography>
            </Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
                Für diese Postleitzahl ist der Mitarbeiter{" "}
                <strong>
                    {workerForCustomer?.User.first_name} {workerForCustomer?.User.last_name}
                </strong>{" "}
                zuständig.
            </Typography>
            <Typography variant="body1">Möchten Sie diesen Mitarbeiter dem Projekt zuweisen?</Typography>
        </div>
    );
};

type InnendienstMitarbeiterAssignmentProps = {
    innendienstWorker: IProfile | undefined;
    existingInnendienstWorkers: IProfile[] | undefined;
};

const InnendienstMitarbeiterAssignment: React.FC<InnendienstMitarbeiterAssignmentProps> = ({
    innendienstWorker,
    existingInnendienstWorkers,
}) => {
    if (!innendienstWorker) {
        return null;
    }

    return (
        <div>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Der ausgewählte Außendienstmitarbeiter hat einen Innendienstmitarbeiter zugewiesen.
                </Typography>
            </Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
                Für diesen Aussendienstmitarbeiter ist der Innendienstmitarbeiter{" "}
                <strong>
                    {innendienstWorker?.User.first_name} {innendienstWorker?.User.last_name}
                </strong>{" "}
                zuständig.
            </Typography>
            <Typography variant="body1">
                {existingInnendienstWorkers && existingInnendienstWorkers.length > 0 ? (
                    <>
                        Diesem Projekt {existingInnendienstWorkers.length === 1 ? "ist" : "sind"} bereits folgende
                        {existingInnendienstWorkers.length === 1 ? "r" : ""} Innendienstmitarbeiter
                        {existingInnendienstWorkers.length === 1 ? "" : ""} zugewiesen:{" "}
                        {existingInnendienstWorkers.length === 1 ? (
                            <strong>
                                {existingInnendienstWorkers[0].User.first_name}{" "}
                                {existingInnendienstWorkers[0].User.last_name}
                            </strong>
                        ) : (
                            <>
                                <strong>
                                    {existingInnendienstWorkers
                                        .slice(0, existingInnendienstWorkers.length - 1)
                                        .map((worker) => `${worker.User.first_name} ${worker.User.last_name}`)
                                        .join(", ")}
                                </strong>
                                {" und "}
                                <strong>
                                    {existingInnendienstWorkers[existingInnendienstWorkers.length - 1].User.first_name}{" "}
                                    {existingInnendienstWorkers[existingInnendienstWorkers.length - 1].User.last_name}
                                </strong>
                            </>
                        )}
                        . Möchten Sie{" "}
                        {existingInnendienstWorkers.length === 1 ? "diesen Mitarbeiter" : "diese Mitarbeiter"} ersetzen,
                        den neuen Mitarbeiter hinzufügen oder nur den Außendienstmitarbeiter hinzufügen?
                    </>
                ) : (
                    "Möchten Sie diesen Mitarbeiter dem Projekt zuweisen?"
                )}
            </Typography>
        </div>
    );
};

const AssignmentAction = {
    Aussendienst: "Aussendienst",
    BothAddInnendienst: "BothAddInnendienst",
    BothReplaceInnendienst: "BothReplaceInnendienst",
    None: "None",
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
type AssignmentAction = (typeof AssignmentAction)[keyof typeof AssignmentAction];

type ProjectEditWorkerAssignmentStepperProps = AussendienstMitarbeiterAssignmentProps &
    InnendienstMitarbeiterAssignmentProps & {
        userInteractionComplete: (assignmentAction: AssignmentAction) => void;
    };

const ProjectEditWorkerAssignmentStepper: React.FC<ProjectEditWorkerAssignmentStepperProps> = ({
    workerForCustomer,
    innendienstWorker,
    existingInnendienstWorkers,
    userInteractionComplete,
}) => {
    const shouldShowInnendienstStep =
        innendienstWorker && !existingInnendienstWorkers?.find((worker) => worker.id === innendienstWorker?.id);

    const steps = [
        { name: "Aussendienstmitarbeiter zuweisen", component: AussendienstMitarbeiterAssignment },
        ...(shouldShowInnendienstStep
            ? [{ name: "Innendienstmitarbeiter zuweisen", component: InnendienstMitarbeiterAssignment }]
            : []),
    ];

    const [activeStep, setActiveStep] = React.useState(0);

    const isStepDisabled = (step: number) => {
        return (
            step === 1 &&
            (!innendienstWorker ||
                existingInnendienstWorkers?.find((worker) => worker.id === innendienstWorker?.id) !== undefined)
        );
    };

    const isStepLast = (step: number) => {
        return step === steps.length - 1;
    };

    if (activeStep >= steps.length) {
        return null;
    }

    const Component = steps[activeStep].component;

    return (
        <>
            <Stepper activeStep={activeStep}>
                {steps.map(({ name }, i) => {
                    const stepProps: { completed?: boolean; disabled?: boolean } = {};

                    if (isStepDisabled(i)) {
                        stepProps.disabled = true;
                    }

                    if (i < activeStep) {
                        stepProps.completed = true;
                    }

                    return (
                        <Step key={name} {...stepProps}>
                            <StepLabel>{name}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>
            <Component
                workerForCustomer={workerForCustomer}
                innendienstWorker={innendienstWorker}
                existingInnendienstWorkers={existingInnendienstWorkers}
            />
            <Stack direction="row" justifyContent={"space-between"} sx={{ mt: 2 }}>
                <Button
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep((prev) => prev - 1)}
                    variant="outlined"
                >
                    Zurück
                </Button>
                <Stack direction="row" spacing={2}>
                    <Button
                        onClick={() => {
                            if (activeStep === 0) {
                                userInteractionComplete(AssignmentAction.None);
                            }
                            if (isStepLast(activeStep)) {
                                userInteractionComplete(AssignmentAction.Aussendienst);
                            }
                        }}
                        variant="text"
                    >
                        Nicht zuweisen
                    </Button>
                    {activeStep !== 0 &&
                        isStepLast(activeStep) &&
                        existingInnendienstWorkers &&
                        (existingInnendienstWorkers?.length ?? 0) > 0 && (
                            <Button
                                onClick={() => {
                                    userInteractionComplete(AssignmentAction.BothReplaceInnendienst);
                                }}
                                variant="contained"
                            >
                                {existingInnendienstWorkers.length > 1 ? `Alle ersetzen` : "Ersetzen"}
                            </Button>
                        )}
                    <Button
                        disabled={isStepDisabled(activeStep)}
                        onClick={() => {
                            if (isStepLast(activeStep) && activeStep !== 0) {
                                userInteractionComplete(AssignmentAction.BothAddInnendienst);
                            } else if (
                                activeStep === 0 &&
                                innendienstWorker &&
                                existingInnendienstWorkers?.find((worker) => worker.id === innendienstWorker?.id) !==
                                    undefined
                            ) {
                                userInteractionComplete(AssignmentAction.Aussendienst);
                            } else {
                                setActiveStep((prev) => prev + 1);
                            }
                        }}
                        variant="contained"
                    >
                        {isStepLast(activeStep) ? "Hinzufügen" : "Ja"}
                    </Button>
                </Stack>
            </Stack>
        </>
    );
};

export default ProjectEditWorkerAssignmentStepper;
